import { mockOfficeAgent } from '../agents';
import { mockConversations } from '../conversations';
import { makeStreamStart, makeBlockStart, makeBlockEnd, makeBlockComplete, makeApprovalRequired, makePlanProgress, makeProgress, makeStreamEnd, splitTextIntoDeltas } from '../sseEvents';
const RESPONSE_ID = 'resp-plan-approval-flow';
const CONV_ID = 'conv-10';
const MD_INTRO_BLOCK_ID = 'blk-plan-md-intro';
const PLAN_ID = 'plan-bulk-reassign-001';
const MD_RESULT_BLOCK_ID = 'blk-plan-md-result';
const UNDO_BLOCK_ID = 'blk-plan-undo';
const introText = 'I can reassign and reschedule these inspections for you. Here is the execution plan:';
const executionPlanBlock = {
    type: 'execution_plan',
    block_id: `blk-plan-${PLAN_ID}`,
    block_index: 1,
    plan_id: PLAN_ID,
    plan_summary: 'Reassign 3 inspections from Tech A to Tech B and reschedule to next week',
    steps: [
        {
            step_index: 0,
            tool: 'reassign_inspection',
            summary: 'Reassign inspection #1042 to Tech B',
            severity: 'standard',
            depends_on: []
        },
        {
            step_index: 1,
            tool: 'reassign_inspection',
            summary: 'Reassign inspection #1043 to Tech B',
            severity: 'standard',
            depends_on: []
        },
        {
            step_index: 2,
            tool: 'reschedule_inspections',
            summary: 'Reschedule all 3 inspections to 2026-03-09',
            severity: 'warning',
            depends_on: [0, 1]
        }
    ],
    total_affected_records: 3,
    reversible: true,
    approval_mode: 'batch',
    confirm_label: 'Approve plan (3 steps)',
    cancel_label: 'Cancel'
};
const resultText = 'All done! I reassigned inspections #1042 and #1043 to Tech B and rescheduled all three to March 9.';
const undoBlock = {
    type: 'undo',
    block_id: UNDO_BLOCK_ID,
    block_index: 6,
    action_id: `action-${PLAN_ID}`,
    summary: 'Undo plan: reassign & reschedule 3 inspections',
    undo_available: true,
    expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
    undo_window_minutes: 30
};
const sseSequence = [
    // Phase 1: Initial markdown explanation
    makeStreamStart(RESPONSE_ID, CONV_ID, mockOfficeAgent),
    makeBlockStart(MD_INTRO_BLOCK_ID, 0, 'markdown'),
    ...splitTextIntoDeltas(MD_INTRO_BLOCK_ID, introText, 20, 60),
    makeBlockEnd(MD_INTRO_BLOCK_ID),
    // Phase 2: approval_required with ExecutionPlanBlock
    makeApprovalRequired(executionPlanBlock, 600),
    // Phase 3: Simulated pause while user reviews, then plan execution progress
    makeProgress('executing', 'Executing plan...', mockOfficeAgent, 3000),
    makePlanProgress({
        plan_id: PLAN_ID,
        step_index: 0,
        total_steps: 3,
        step_summary: 'Reassign inspection #1042 to Tech B',
        step_status: 'executing'
    }, 400),
    makePlanProgress({
        plan_id: PLAN_ID,
        step_index: 0,
        total_steps: 3,
        step_summary: 'Reassign inspection #1042 to Tech B',
        step_status: 'success'
    }, 800),
    makePlanProgress({
        plan_id: PLAN_ID,
        step_index: 1,
        total_steps: 3,
        step_summary: 'Reassign inspection #1043 to Tech B',
        step_status: 'executing'
    }, 300),
    makePlanProgress({
        plan_id: PLAN_ID,
        step_index: 1,
        total_steps: 3,
        step_summary: 'Reassign inspection #1043 to Tech B',
        step_status: 'success'
    }, 800),
    makePlanProgress({
        plan_id: PLAN_ID,
        step_index: 2,
        total_steps: 3,
        step_summary: 'Reschedule all 3 inspections to 2026-03-09',
        step_status: 'executing'
    }, 300),
    makePlanProgress({
        plan_id: PLAN_ID,
        step_index: 2,
        total_steps: 3,
        step_summary: 'Reschedule all 3 inspections to 2026-03-09',
        step_status: 'success'
    }, 1000),
    // Phase 4: Result markdown
    makeBlockStart(MD_RESULT_BLOCK_ID, 5, 'markdown'),
    ...splitTextIntoDeltas(MD_RESULT_BLOCK_ID, resultText, 20, 60),
    makeBlockEnd(MD_RESULT_BLOCK_ID),
    // Phase 5: Undo block
    makeBlockComplete(undoBlock, 400),
    // Phase 6: Stream ends
    makeStreamEnd(RESPONSE_ID, 7, mockOfficeAgent)
];
export const scenario = {
    name: 'Office Assistant — Plan Approval Flow (Multi-Action)',
    description: 'Exercises the full ExecutionPlanBlock approval flow: ' +
        'intro markdown, execution_plan approval_required, plan_progress step-by-step updates, ' +
        'result markdown, and undo block. Demonstrates the multi-action plan approval UX.',
    conversations: mockConversations,
    userMessage: 'Reassign the 3 inspections from Tech A to Tech B and reschedule to next week',
    agentIdentity: mockOfficeAgent,
    sseSequence
};
